import { FileText, FlaskConical, Users, ArrowRight, Globe, Microscope, Lightbulb } from "lucide-react"
import { Button } from "../../components/ui/button"
import { Link } from "react-router-dom"

export default function CentreRecherche() {
    return (
        <div className="min-h-screen bg-background font-sans text-foreground antialiased">
            {/* Hero Section - Standard (ref: DroitEcoGestion) */}
            <header className="relative min-h-[60vh] flex items-center bg-primary overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/80 to-transparent" />
                </div>
                <div className="container relative z-10 py-20">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-4 mb-8 translate-y-4 animate-in fade-in duration-700 fill-mode-forwards">
                            <span className="text-[10px] uppercase tracking-[0.3em] text-accent font-black">Innovation</span>
                            <div className="h-px w-12 bg-accent/50" />
                            <span className="text-[10px] text-primary-foreground/60 uppercase tracking-widest font-bold">Centre de Recherche</span>
                        </div>
                        <h1 className="heading-1 mb-8 leading-[0.95] font-serif tracking-tight text-white translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-200">
                            <span className="block italic font-light text-accent/90">Découvrir,</span>
                            <span className="block text-white mt-2">Comprendre, Innover</span>
                        </h1>
                        <div className="border-l-4 border-accent pl-8 mb-12 max-w-2xl translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-500">
                            <p className="text-lg md:text-xl font-light leading-relaxed text-primary-foreground/90">
                                Un pôle d'excellence pour l'innovation scientifique et le développement technologique au service de la société.
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-6 items-center translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-700">
                            <Button className="bg-accent text-accent-foreground px-10 py-7 text-sm font-black transition-all duration-300 border-b-4 border-black/20 rounded-xl">
                                <Link to="/recherche/publications">Nos Publications</Link>
                                <FileText className="w-5 h-5 ml-3" />
                            </Button>
                            {/* <Button variant="ghost" className="text-white border-2 border-white/20 hover:bg-white/10 hover:border-white px-10 py-7 text-sm font-black rounded-xl backdrop-blur-sm">
                                Appels à Projets
                            </Button> */}
                        </div>
                    </div>
                </div>
                <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce opacity-20">
                    <div className="w-px h-12 bg-white" />
                </div>
            </header>

            {/* Quick Stats Bar */}
            <div className="border-y border-border bg-secondary/10">
                <div className="container py-4 flex flex-wrap items-center gap-8">
                    {[
                        { val: "50+", label: "Chercheurs" },
                        { val: "12", label: "Laboratoires" },
                        { val: "20+", label: "Projets Actifs" },
                        { val: "INTL", label: "Coopération" }
                    ].map((stat, i) => (
                        <div key={i} className="flex items-center gap-3">
                            <span className="text-xl font-black text-primary">{stat.val}</span>
                            <span className="text-[10px] text-muted-foreground uppercase font-black tracking-widest">{stat.label}</span>
                            {i < 3 && <span className="hidden lg:block text-border ml-4 font-light">/</span>}
                        </div>
                    ))}
                </div>
            </div>

            <article className="container section-padding-sm">
                <div className="grid lg:grid-cols-[250px_1fr] gap-12 lg:gap-20">
                    {/* Sidebar */}
                    <aside className="lg:sticky lg:top-8 lg:self-start hidden lg:block">
                        <nav className="space-y-1">
                            <p className="text-[10px] uppercase tracking-[0.2em] font-black text-muted-foreground mb-6 pb-2 border-b border-border">
                                Sommaire
                            </p>
                            {["Axes", "Équipes", "Équipements", "Collaborations"].map((item, i) => (
                                <div key={i} className="flex items-start gap-3 py-2 text-xs font-bold text-muted-foreground">
                                    <span className="text-accent font-black">0{i + 1}</span>
                                    {item}
                                </div>
                            ))}
                        </nav>
                    </aside>

                    {/* Main Content */}
                    <main className="space-y-16">
                        <section>
                            <div className="flex justify-between items-end mb-8">
                                <h2 className="heading-4 text-foreground flex items-center gap-3">
                                    <div className="w-1 h-6 bg-accent rounded-full"></div>
                                    Axes Stratégiques
                                </h2>
                                <Button variant="link" className="text-[10px] font-black uppercase tracking-widest p-0 h-auto">
                                    Voir Tout <ArrowRight className="w-3 h-3 ml-1" />
                                </Button>
                            </div>

                            <div className="grid md:grid-cols-2 gap-6">
                                {[
                                    { title: "Agriculture Durable", icon: Globe, desc: "Sécurité alimentaire et adaptation aux changements climatiques." },
                                    { title: "Santé Publique", icon: Microscope, desc: "Recherches sur les maladies endémiques et systèmes de santé." },
                                    { title: "Énergies Vertes", icon: Lightbulb, desc: "Innovation en énergies renouvelables pour le développement rural." },
                                    { title: "Socio-économie", icon: Users, desc: "Impact des technologies sur le développement communautaire." }
                                ].map((axis, i) => (
                                    <div key={i} className="interactive-card p-6 border-l-4 border-primary group">
                                        <axis.icon className="w-10 h-10 text-primary mb-4 opacity-50 group-hover:opacity-100 group-hover:text-accent transition-smooth" />
                                        <h3 className="text-sm font-bold uppercase tracking-tight mb-2">{axis.title}</h3>
                                        <p className="text-xs text-muted-foreground leading-relaxed">
                                            {axis.desc}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </section>

                        <section className="bg-primary/5 p-8 border border-primary/10 rounded-md relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-accent/5 rounded-full -mr-16 -mt-16" />
                            <h3 className="heading-4 text-primary mb-4">Infrastructures de Recherche</h3>
                            <p className="text-sm text-muted-foreground mb-6 max-w-2xl leading-relaxed">
                                Le centre dispose de plateaux techniques modernes, incluant des laboratoires de biologie moléculaire, des salles informatiques haute performance et des stations expérimentales agricoles.
                            </p>
                            <div className="flex flex-wrap gap-3">
                                {["Bio-Tech", "Data-Science", "Agro-Lab", "HPC"].map((tag, i) => (
                                    <span key={i} className="px-3 py-1 bg-white border border-border text-[10px] font-black uppercase tracking-wider rounded-sm shadow-sm transition-smooth hover:border-accent">
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        </section>
                    </main>
                </div>
            </article>
        </div>
    )
}
